"use client";

/**
 * Brand marks for the model routes P/9 serves.
 *
 * Kept apart from `./icons.tsx` on purpose. Those are ours: one 24px grid, one
 * 1.6 stroke weight, drawn to match each other. These are other people's
 * trademarks, reproduced to identify the model a row is about, and they are not
 * ours to redraw "on grid" or restyle. Mixing the two files would invite
 * exactly that.
 *
 * **In brand colour, and unenclosed.** They are the one deliberate exception to
 * the warm-monochrome rule in `kb/design.md`, because a trademark recoloured to
 * fit our palette is no longer the trademark. They also sit bare rather than in
 * a bordered tile: a box around a logo reads as our chrome wrapping someone
 * else's mark.
 *
 * Qwen carries its official purple gradient, Kimi its official blue, DeepSeek
 * its blue and Nvidia its green. OpenAI's mark has no colour of its own: the
 * brand is black, inverted to white on dark grounds, which is exactly what
 * `currentColor` against `--foreground` already produces. Forcing a hex there
 * would break one of the two themes. Z.ai is the same case, and the icon set
 * publishes no coloured variant of it for the same reason.
 *
 * Not every lab in the catalogue has a mark here. Thinking Machines Lab, whose
 * Inkling weights arrived on 2026-08-30, ships a wordmark rather than a glyph
 * and has no artwork in the set, so those two rows carry `logo: null` and draw
 * no mark at all. Setting their name in our own type would be inventing a
 * trademark, which is worse than leaving the slot empty.
 *
 * Path data, gradient stops and hexes are the official artwork, verbatim from
 * the `@lobehub/icons` set (all marks 24x24). Do not hand-edit the `d`
 * attributes or the colours: re-fetch instead, or the mark stops being the
 * mark.
 */

import { useId } from "react";

type MarkProps = { size?: number; className?: string };

const OPENAI_PATH =
  "M9.205 8.658v-2.26c0-.19.072-.333.238-.428l4.543-2.616c.619-.357 1.356-.523 2.117-.523 2.854 0 4.662 2.212 4.662 4.566 0 .167 0 .357-.024.547l-4.71-2.759a.797.797 0 00-.856 0l-5.97 3.473zm10.609 8.8V12.06c0-.333-.143-.57-.429-.737l-5.97-3.473 1.95-1.118a.433.433 0 01.476 0l4.543 2.617c1.309.76 2.189 2.378 2.189 3.948 0 1.808-1.07 3.473-2.76 4.163zM7.802 12.703l-1.95-1.142c-.167-.095-.239-.238-.239-.428V5.899c0-2.545 1.95-4.472 4.591-4.472 1 0 1.927.333 2.712.928L8.23 5.067c-.285.166-.428.404-.428.737v6.898zM12 15.128l-2.795-1.57v-3.33L12 8.658l2.795 1.57v3.33L12 15.128zm1.796 7.23c-1 0-1.927-.332-2.712-.927l4.686-2.712c.285-.166.428-.404.428-.737v-6.898l1.974 1.142c.167.095.238.238.238.428v5.233c0 2.545-1.974 4.472-4.614 4.472zm-5.637-5.303l-4.544-2.617c-1.308-.761-2.188-2.378-2.188-3.948A4.482 4.482 0 014.21 6.327v5.423c0 .333.143.571.428.738l5.947 3.449-1.95 1.118a.432.432 0 01-.476 0zm-.262 3.9c-2.688 0-4.662-2.021-4.662-4.519 0-.19.024-.38.047-.57l4.686 2.71c.286.167.571.167.856 0l5.97-3.448v2.26c0 .19-.07.333-.237.428l-4.543 2.616c-.619.357-1.356.523-2.117.523zm5.899 2.83a5.947 5.947 0 005.827-4.756C22.287 18.339 24 15.84 24 13.296c0-1.665-.713-3.282-1.998-4.448.119-.5.19-.999.19-1.498 0-3.401-2.759-5.947-5.946-5.947-.642 0-1.26.095-1.88.31A5.962 5.962 0 0010.205 0a5.947 5.947 0 00-5.827 4.757C1.713 5.447 0 7.945 0 10.49c0 1.666.713 3.283 1.998 4.448-.119.5-.19 1-.19 1.499 0 3.401 2.759 5.946 5.946 5.946.642 0 1.26-.095 1.88-.309a5.96 5.96 0 004.162 1.713z";

const KIMI_PATHS = [
  "M21.846 0a1.923 1.923 0 110 3.846H20.15a.226.226 0 01-.227-.226V1.923C19.923.861 20.784 0 21.846 0z",
  "M11.065 11.199l7.257-7.2c.137-.136.06-.41-.116-.41H14.3a.164.164 0 00-.117.051l-7.82 7.756c-.122.12-.302.013-.302-.179V3.82c0-.127-.083-.23-.185-.23H3.186c-.103 0-.186.103-.186.23V19.77c0 .128.083.23.186.23h2.69c.103 0 .186-.102.186-.23v-3.25c0-.069.025-.135.069-.178l2.424-2.406a.158.158 0 01.205-.023l6.484 4.772a7.677 7.677 0 003.453 1.283c.108.012.2-.095.2-.23v-3.06c0-.117-.07-.212-.164-.227a5.028 5.028 0 01-2.027-.807l-5.613-4.064c-.117-.078-.132-.279-.028-.381z",
];

/** Kimi's official brand blue, from the coloured variant of the mark. */
const KIMI_BLUE = "#1783FF";

const ZAI_PATH =
  "M12.105 2L9.927 4.953H.653L2.83 2h9.276zM23.254 19.048L21.078 22h-9.242l2.174-2.952h9.244zM24 2L9.264 22H0L14.736 2H24z";

const DEEPSEEK_PATH =
  "M23.748 4.482c-.254-.124-.364.113-.512.234-.051.039-.094.09-.137.136-.372.397-.806.657-1.373.626-.829-.046-1.537.214-2.163.848-.133-.782-.575-1.248-1.247-1.548-.352-.156-.708-.311-.955-.65-.172-.241-.219-.51-.305-.774-.055-.16-.11-.323-.293-.35-.2-.031-.278.136-.356.276-.313.572-.434 1.202-.422 1.84.027 1.436.633 2.58 1.838 3.393.137.093.172.187.129.323-.082.28-.18.552-.266.833-.055.179-.137.217-.329.14a5.526 5.526 0 01-1.736-1.18c-.857-.828-1.631-1.742-2.597-2.458a11.365 11.365 0 00-.689-.471c-.985-.957.13-1.743.388-1.836.27-.098.093-.432-.779-.428-.872.004-1.67.295-2.687.684a3.055 3.055 0 01-.465.137 9.597 9.597 0 00-2.883-.102c-1.885.21-3.39 1.102-4.497 2.623C.082 8.606-.231 10.684.152 12.85c.403 2.284 1.569 4.175 3.36 5.653 1.858 1.533 3.997 2.284 6.438 2.14 1.482-.085 3.133-.284 4.994-1.86.47.234.962.327 1.78.397.63.059 1.236-.03 1.705-.128.735-.156.684-.837.419-.961-2.155-1.004-1.682-.595-2.113-.926 1.096-1.296 2.746-2.642 3.392-7.003.05-.347.007-.565 0-.845-.004-.17.035-.237.23-.256a4.173 4.173 0 001.545-.475c1.396-.763 1.96-2.015 2.093-3.517.02-.23-.004-.467-.247-.588zM11.581 18c-2.089-1.642-3.102-2.183-3.52-2.16-.392.024-.321.471-.235.763.09.288.207.486.371.739.114.167.192.416-.113.603-.673.416-1.842-.14-1.897-.167-1.361-.802-2.5-1.86-3.301-3.307-.774-1.393-1.224-2.887-1.298-4.482-.02-.386.093-.522.477-.592a4.696 4.696 0 011.529-.039c2.132.312 3.946 1.265 5.468 2.774.868.86 1.525 1.887 2.202 2.891.72 1.066 1.494 2.082 2.48 2.914.348.292.625.514.891.677-.802.09-2.14.11-3.054-.614zm1-6.44a.306.306 0 01.415-.287.302.302 0 01.2.288.306.306 0 01-.31.307.303.303 0 01-.304-.308zm3.11 1.596c-.2.081-.399.151-.59.16a1.245 1.245 0 01-.798-.254c-.274-.23-.47-.358-.552-.758a1.73 1.73 0 01.016-.588c.07-.327-.008-.537-.239-.727-.187-.156-.426-.199-.688-.199a.559.559 0 01-.254-.078c-.11-.054-.2-.19-.114-.358.028-.054.16-.186.192-.21.356-.202.767-.136 1.146.016.352.144.618.408 1.001.782.391.451.462.576.685.914.176.265.336.537.445.848.067.195-.019.354-.25.452z";

/** DeepSeek's official brand blue, from the coloured variant of the mark. */
const DEEPSEEK_BLUE = "#4D6BFE";

const NVIDIA_PATH =
  "M10.212 8.976V7.62c.127-.01.256-.017.388-.021 3.596-.117 5.957 3.184 5.957 3.184s-2.548 3.647-5.282 3.647a3.227 3.227 0 01-1.063-.175v-4.109c1.4.174 1.681.812 2.523 2.258l1.873-1.627a4.905 4.905 0 00-3.67-1.846 6.594 6.594 0 00-.729.044m0-4.476v2.025c.13-.01.259-.019.388-.024 5.002-.174 8.261 4.226 8.261 4.226s-3.743 4.69-7.643 4.69c-.338 0-.675-.031-1.007-.092v1.25c.278.038.558.057.838.057 3.629 0 6.253-1.91 8.794-4.169.421.347 2.146 1.193 2.501 1.564-2.416 2.083-8.048 3.763-11.24 3.763-.308 0-.603-.02-.894-.048V19.5H24v-15H10.21zm0 9.756v1.068c-3.356-.616-4.287-4.21-4.287-4.21a7.173 7.173 0 014.287-2.138v1.172h-.005a3.182 3.182 0 00-2.502 1.178s.615 2.276 2.507 2.931m-5.961-3.3c1.436-1.935 3.604-3.148 5.961-3.336V6.523C5.81 6.887 2 10.723 2 10.723s2.158 6.427 8.21 7.015v-1.166C5.77 16 4.25 10.958 4.25 10.958h-.002z";

/** Nvidia's official brand green, from the coloured variant of the mark. */
const NVIDIA_GREEN = "#74B71B";

const QWEN_PATH =
  "M12.604 1.34c.393.69.784 1.382 1.174 2.075a.18.18 0 00.157.091h5.552c.174 0 .322.11.446.327l1.454 2.57c.19.337.24.478.024.837-.26.43-.513.864-.76 1.3l-.367.658c-.106.196-.223.28-.04.512l2.652 4.637c.172.301.111.494-.043.77-.437.785-.882 1.564-1.335 2.34-.159.272-.352.375-.68.37-.777-.016-1.552-.01-2.327.016a.099.099 0 00-.081.05 575.097 575.097 0 01-2.705 4.74c-.169.293-.38.363-.725.364-.997.003-2.002.004-3.017.002a.537.537 0 01-.465-.271l-1.335-2.323a.09.09 0 00-.083-.049H4.982c-.285.03-.553-.001-.805-.092l-1.603-2.77a.543.543 0 01-.002-.54l1.207-2.12a.198.198 0 000-.197 550.951 550.951 0 01-1.875-3.272l-.79-1.395c-.16-.31-.173-.496.095-.965.465-.813.927-1.625 1.387-2.436.132-.234.304-.334.584-.335a338.3 338.3 0 012.589-.001.124.124 0 00.107-.063l2.806-4.895a.488.488 0 01.422-.246c.524-.001 1.053 0 1.583-.006L11.704 1c.341-.003.724.032.9.34zm-3.432.403a.06.06 0 00-.052.03L6.254 6.788a.157.157 0 01-.135.078H3.253c-.056 0-.07.025-.041.074l5.81 10.156c.025.042.013.062-.034.063l-2.795.015a.218.218 0 00-.2.116l-1.32 2.31c-.044.078-.021.118.068.118l5.716.008c.046 0 .08.02.104.061l1.403 2.454c.046.081.092.082.139 0l5.006-8.76.783-1.382a.055.055 0 01.096 0l1.424 2.53a.122.122 0 00.107.062l2.763-.02a.04.04 0 00.035-.02.041.041 0 000-.04l-2.9-5.086a.108.108 0 010-.113l.293-.507 1.12-1.977c.024-.041.012-.062-.035-.062H9.2c-.059 0-.073-.026-.043-.077l1.434-2.505a.107.107 0 000-.114L9.225 1.774a.06.06 0 00-.053-.031zm6.29 8.02c.046 0 .058.02.034.06l-.832 1.465-2.613 4.585a.056.056 0 01-.05.029.058.058 0 01-.05-.029L8.498 9.841c-.02-.034-.01-.052.028-.054l.216-.012 6.722-.012z";

/**
 * OpenAI: solid, in the current ink.
 *
 * `currentColor` is not a monochrome compromise here, it is the brand: the mark
 * is black, and white is its sanctioned inversion on dark grounds. Callers put
 * it on `text-foreground`, which resolves to near-black in light mode and
 * near-white in dark.
 */
export function OpenAiMark({ size = 20, className }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      fillRule="evenodd"
      role="img"
      aria-label="OpenAI"
      className={className}
    >
      <path d={OPENAI_PATH} />
    </svg>
  );
}

/**
 * Qwen: the official purple gradient, left to right.
 *
 * The gradient id comes from `useId` rather than a constant. Two marks on one
 * page would otherwise declare the same id twice, and a duplicate id is a
 * coin-flip about which definition the browser resolves. That stopped being
 * hypothetical on 2026-08-20: the catalogue now carries two Qwen routes, so
 * the Models table really does draw this mark twice.
 */
export function QwenMark({ size = 20, className }: MarkProps) {
  const gradientId = `qwen-gradient-${useId()}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label="Qwen"
      className={className}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="#6336E7" stopOpacity="0.84" />
          <stop offset="100%" stopColor="#6F69F7" stopOpacity="0.84" />
        </linearGradient>
      </defs>
      <path d={QWEN_PATH} fill={`url(#${gradientId})`} fillRule="nonzero" />
    </svg>
  );
}

/**
 * Kimi: the official brand blue, flat.
 *
 * Two subpaths (the dot and the wordless glyph) sharing one fill, so unlike
 * Qwen it needs no gradient and therefore no `useId`. `#1783FF` reads on both
 * themes, which is why it is safe to hardcode where OpenAI's black is not.
 */
export function KimiMark({ size = 20, className }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={KIMI_BLUE}
      fillRule="evenodd"
      role="img"
      aria-label="Kimi"
      className={className}
    >
      {KIMI_PATHS.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/**
 * Z.ai: the angular Z, in the current ink.
 *
 * Same reasoning as OpenAI. The set publishes no coloured variant of this mark
 * because the brand does not have one: it is black artwork, inverted to white
 * on dark grounds, which `currentColor` against `--foreground` already gives.
 * Z.ai ships the GLM weights; the mark on the model cards is theirs.
 */
export function ZaiMark({ size = 20, className }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      fillRule="evenodd"
      role="img"
      aria-label="Z.ai"
      className={className}
    >
      <path d={ZAI_PATH} />
    </svg>
  );
}

/**
 * DeepSeek: the official brand blue, flat.
 *
 * One path, so no gradient and no `useId`. `#4D6BFE` reads on both themes,
 * which is why it is safe to hardcode where OpenAI's black is not.
 */
export function DeepSeekMark({ size = 20, className }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={DEEPSEEK_BLUE}
      fillRule="evenodd"
      role="img"
      aria-label="DeepSeek"
      className={className}
    >
      <path d={DEEPSEEK_PATH} />
    </svg>
  );
}

/**
 * Nvidia: the official brand green, flat.
 *
 * `fillRule` is `nonzero` here, not `evenodd`: the eye of the mark is drawn as
 * an overlapping subpath, and the even-odd rule punches it out.
 */
export function NvidiaMark({ size = 20, className }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={NVIDIA_GREEN}
      fillRule="nonzero"
      role="img"
      aria-label="Nvidia"
      className={className}
    >
      <path d={NVIDIA_PATH} />
    </svg>
  );
}

/**
 * Which mark belongs to a catalogue entry.
 *
 * Keyed by an explicit `logo` field on the model rather than sniffed from the
 * route id. The ids are ours (`paranine/...`), so a substring match
 * on "qwen" would work today and break the first time a route is renamed, and
 * silently: a missing logo is invisible, not an error.
 */
export const MODEL_MARKS = {
  openai: OpenAiMark,
  qwen: QwenMark,
  kimi: KimiMark,
  zai: ZaiMark,
  deepseek: DeepSeekMark,
  nvidia: NvidiaMark,
} as const;

export type ModelMark = keyof typeof MODEL_MARKS;

/**
 * The mark for a model, drawn bare.
 *
 * No tile, no border, no plate. An earlier version boxed each mark in a
 * bordered square to even out their optical weight (one is a dense six-fold
 * knot, the other an open lattice); the box read as our chrome framing someone
 * else's logo, which is the wrong relationship. They sit directly on the
 * surface now, at a size that lets both breathe.
 *
 * Draws nothing when the model has no mark, but still **occupies the slot**.
 * Returning null outright, which this did until 2026-08-30, was fine while
 * every row had a mark; the catalogue now carries rows that do not (see the
 * note at the top of this file), and an absent flex item is not the same as an
 * empty one. It collapsed the name column's left edge on the dashboard rows
 * and pushed the pool tag on the landing card from the right of the header to
 * the left, because a `justify-between` header with one child aligns it to the
 * start. The space is reserved and blank: no square, no border, no rule.
 */
export function ModelLogo({
  logo,
  size = 22,
  className,
}: {
  logo: ModelMark | null;
  size?: number;
  className?: string;
}) {
  if (!logo) {
    return (
      <span
        aria-hidden="true"
        className={`block shrink-0 ${className ?? ""}`}
        style={{ width: size, height: size }}
      />
    );
  }
  const Mark = MODEL_MARKS[logo];
  return <Mark size={size} className={`shrink-0 text-foreground ${className ?? ""}`} />;
}

/* ------------------------------------------------------- upstream providers */

/**
 * Marks for the upstream providers a customer can bring their own key for.
 *
 * A different list from the one above, and deliberately a separate map: those
 * identify the *lab that made the weights* on a catalogue row, these identify
 * the *account a request is billed to* on the BYOK page. OpenAI happens to
 * appear in both and reuses the one mark; nothing else overlaps.
 *
 * The same provenance rule applies. Anthropic's is its official wordless mark,
 * verbatim from the icon set. Google's is the official four-colour G, in its
 * own brand colours for the same reason Qwen keeps its gradient: a trademark
 * recoloured to fit our palette stops being the trademark. DeepInfra publishes
 * no glyph in any set vendored here, so it falls back to `ProviderInitial`: a
 * lettered tile in our own chrome, which is a placeholder rather than an
 * invented trademark. Replace it the moment real artwork is vendored.
 */
const ANTHROPIC_PATH =
  "M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z";

/**
 * Anthropic: solid, in the current ink.
 *
 * The same case as OpenAI's. The mark is published black, white is its
 * sanctioned inversion on dark grounds, and `currentColor` on `--foreground`
 * is already both. Anthropic's coral accent belongs to its typography and
 * surfaces, not to this glyph.
 */
export function AnthropicMark({ size = 20, className }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      role="img"
      aria-label="Anthropic"
      className={className}
    >
      <path d={ANTHROPIC_PATH} />
    </svg>
  );
}

/** Google's four brand colours, from the official artwork. */
const GOOGLE_BLUE = "#4285F4";
const GOOGLE_GREEN = "#34A853";
const GOOGLE_YELLOW = "#FBBC05";
const GOOGLE_RED = "#EA4335";

/**
 * Google, for Vertex AI.
 *
 * The corporate G rather than a Vertex-specific product icon: Vertex's own
 * artwork is not in any set vendored here, and the G is the mark Google
 * publishes for identifying itself. Four paths, four fixed hexes, no
 * `currentColor`: this one is coloured in both themes.
 */
export function GoogleMark({ size = 20, className }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      role="img"
      aria-label="Google"
      className={className}
    >
      <path
        fill={GOOGLE_BLUE}
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill={GOOGLE_GREEN}
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill={GOOGLE_YELLOW}
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill={GOOGLE_RED}
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

/**
 * Keyed by the vault's own `provider` values, so the BYOK page can look a mark
 * up straight from the API's string with no second vocabulary in between.
 * A provider with no artwork is `null`, not absent: the difference is a mark
 * we chose not to draw versus a provider nobody has mapped yet.
 */
export const PROVIDER_MARKS: Record<string, ((p: MarkProps) => React.ReactElement) | null> = {
  anthropic: AnthropicMark,
  openai: OpenAiMark,
  vertex: GoogleMark,
  deepinfra: null,
};

/**
 * The stand-in for a provider with no vendored artwork.
 *
 * Deliberately **our** chrome and not a mark: a bordered tile in our own sans,
 * the same treatment an avatar gets for a person with no photo. That is the
 * point of the distinction. A glyph drawn from memory, or a name set in our
 * display face and placed where a logo goes, would read as the provider's
 * trademark and would be one we invented. A lettered tile reads as a
 * placeholder, which is exactly what it is.
 *
 * Every real mark in this file sits bare and unenclosed, precisely so our
 * chrome never frames somebody else's logo. The box here is the tell: a tile
 * beside a real mark means we have not vendored that provider's artwork yet,
 * and the fix is to add it rather than to keep the tile.
 */
function ProviderInitial({
  label,
  size = 22,
  className,
}: {
  label: string;
  size?: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-md border border-border bg-card-2 font-sans font-medium leading-none text-muted ${className ?? ""}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.5) }}
    >
      {label.trim().charAt(0).toUpperCase() || "?"}
    </span>
  );
}

/**
 * The mark for an upstream provider, drawn bare and reserving its slot.
 *
 * Same contract as `ModelLogo`: the slot is always occupied, so a row of
 * provider cards keeps one alignment whether or not each provider in it has
 * artwork. Where `ModelLogo` leaves that slot blank, this draws a lettered
 * tile, because the two absences read differently: a catalogue row without a
 * mark still carries the lab's name in the cell beside it, while a provider
 * card is scanned by logo first and an empty square there reads as a rendering
 * fault rather than as a decision.
 *
 * Pass `label` for the tile's letter (the provider's display name). Without
 * one the slot stays blank, which is the right answer for a provider this
 * component has never heard of.
 */
export function ProviderLogo({
  provider,
  label,
  size = 22,
  className,
}: {
  provider: string;
  label?: string;
  size?: number;
  className?: string;
}) {
  const Mark = PROVIDER_MARKS[provider] ?? null;
  if (!Mark) {
    if (label) return <ProviderInitial label={label} size={size} className={className} />;
    return (
      <span
        aria-hidden="true"
        className={`block shrink-0 ${className ?? ""}`}
        style={{ width: size, height: size }}
      />
    );
  }
  return <Mark size={size} className={`shrink-0 text-foreground ${className ?? ""}`} />;
}
